import { Card, Grid, InputLabel, Tooltip, IconButton, Icon } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import { Form, Formik } from "formik";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { post, get, postNoResponse } from "utils/WebService";
import { toDateTimeNoSeconds } from "utils/DateUtils";
import MDSelect from 'components/MDSelect';
import DeadlinesCreateEdit from "./DeadlinesCreateEdit";
import { useMaterialUIController } from "context";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import { useUser } from "context/UserContext";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import HistoryConsult from "views/history/HistoryConsult";

const Deadlines = ({ id, idDeadLine = null, table = null }) => {

  const [data, setData] = useState([]);
  const [pages, setPages] = useState(0);
  const [loadingTable, setLoadingTable] = useState(true);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [aplicar, setAplicar] = useState(false);
  const [isCreateEdit, setIsCreateEdit] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [isLoadingUsuarios, setIsLoadingUsuarios] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewHistory, setViewHistory] = useState(false);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const {
    statusPrazoEnum,
    tipoEventoEnum,
    userId
  } = useUser();

  const statusPrazoOptions = [
    { id: null, code: null, name: "Todos" },
    ...(statusPrazoEnum || [])
  ];


  const tableState = useRef({
    casoProcessualId: id,
    pageSize: 10,
    pageIndex: 0,
    sorting: [],
    filtered: [],
    dataInicio: null,
    dataFim: null,
    dataInicioPublicacao: null,
    dataFimPublicacao: null,
    tipoPrazo: null,
    statusPrazo: null,
    pessoaId: null
  });

  useEffect(() => {
    if (idDeadLine) {
      setSelectedId(idDeadLine);
      setViewHistory(true);
    }
  }, [idDeadLine]);

  useEffect(() => {
    if (id) { fetchDeadlines({ ...tableState.current }); }

    setIsLoadingUsuarios(true);

    get(`/equipe/usuarios/${userId}`).then(({ list }) => {
      setUsuarios(list);
      setIsLoadingUsuarios(false);
    }).catch(() => {
      setUsuarios([]);
      setIsLoadingUsuarios(false);
    });
  }, [id]);

  const columns = [
    {
      header: "Data",
      accessorKey: "data",
      cell: info => {
        return <DefaultCell>{toDateTimeNoSeconds(info.getValue())}</DefaultCell>;
      }
    },
    {
      header: "Criado ",
      accessorKey: "dataCriacao",
      cell: info => {
        const date = new Date(info.getValue());
        return <DefaultCell>{date.toLocaleDateString()}</DefaultCell>;
      }
    },
    {
      header: "Tipo de Prazo",
      accessorKey: "tipoPrazo",
      cell: info => {
        const tipo = tipoEventoEnum?.find(ht => ht.code === info.getValue());
        return <DefaultCell>{tipo?.name || info.getValue()}</DefaultCell>;
      },
    },
    {
      header: "Descrição",
      accessorKey: "descricao",
      cell: info => <DefaultCell>{info.getValue()}</DefaultCell>
    },
    {
      header: "dias p/ avisar",
      accessorKey: "qtdDiasAvisar",
      cell: info => <DefaultCell>{info.getValue()?.toString()}</DefaultCell>
    },
    {
      header: "Status",
      accessorKey: "statusPrazo",
      cell: info => {
        const statusPrazo = statusPrazoEnum?.find(ht => ht.code === info.getValue());
        return <DefaultCell>{statusPrazo?.name || info.getValue()}</DefaultCell>;
      },
    },
    {
      header: "Pessoa",
      accessorKey: "pessoa",
      cell: info => <DefaultCell>{info.getValue()?.nome}</DefaultCell>
    },
    {
      header: "Processo",
      accessorKey: "casoProcessual",
      cell: info => <DefaultCell>{info.getValue()?.nome ?? "-"}</DefaultCell>
    },
    {
      header: "ações",
      isSorted: false,
      cell: info => (
        <>
          <Tooltip title="Editar">
            <IconButton
              onClick={() => {
                setSelectedId(info.row.original.id);
                setIsCreateEdit(true);
              }}>
              <MDTypography variant="h4">
                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
              </MDTypography>
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton
              onClick={() => {
                handleDelete(info.row.original.id);
              }}>
              <MDTypography variant="h4">
                <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
              </MDTypography>
            </IconButton>
          </Tooltip>
          <Tooltip title="Log">
            <IconButton
              onClick={() => {
                setSelectedId(info.row.original.id)
                setViewHistory(true)
              }}>
              <MDTypography variant="h4">
                <Icon color="inherit" sx={{ marginRight: 1 }}>search</Icon>
              </MDTypography>
            </IconButton>
          </Tooltip>
        </>
      ),
      width: "15%"
    },
  ];

  const fetchDeadlines = useCallback(
    ({
      pageSize,
      pageIndex,
      sorting,
      filtered,
      dataInicio,
      dataFim,
      tipoPrazo,
      statusPrazo,
      pessoaId,
      dataInicioPublicacao,
      dataFimPublicacao
    }) => {
      setLoadingTable(true);

      const updatedTableState = {
        pageSize: pageSize ?? tableState.current.pageSize,
        pageIndex: pageIndex ?? tableState.current.pageIndex,
        sorting: sorting ?? tableState.current.sorting,
        filtered: filtered && filtered.length > 0 ? filtered : tableState.current.filtered,
        dataInicio: dataInicio ?? tableState.current.dataInicio,
        dataFim: dataFim ?? tableState.current.dataFim,
        dataInicioPublicacao: dataInicioPublicacao ?? tableState.current.dataInicioPublicacao,
        dataFimPublicacao: dataFimPublicacao ?? tableState.current.dataFimPublicacao,
        tipoPrazo: tipoPrazo ?? tableState.current.tipoPrazo,
        statusPrazo: statusPrazo ?? tableState.current.statusPrazo,
        pessoaId: pessoaId ?? tableState.current.pessoaId,

        casoProcessualId: tableState.current.casoProcessualId,
      };

      tableState.current = updatedTableState;

      const body = {
        page: updatedTableState.pageIndex,
        pageSize: updatedTableState.pageSize,
        sorted: updatedTableState.sorting,
        filtered: updatedTableState.filtered,
        casoProcessualId: updatedTableState.casoProcessualId,
        dataInicio: updatedTableState.dataInicio,
        dataFim: updatedTableState.dataFim,
        dataInicioPublicacao: updatedTableState.dataInicioPublicacao,
        dataFimPublicacao: updatedTableState.dataFimPublicacao,
        tipoPrazo: updatedTableState.tipoPrazo,
        statusPrazo: updatedTableState.statusPrazo,
        pessoaId: updatedTableState.pessoaId,
      };

      post("/prazo/list", body)
        .then(({ data, pages, recordsTotal }) => {
          setPages(pages);
          setData(data);
          setRecordsTotal(recordsTotal);
          setLoadingTable(false);
          setAplicar(false);
        })
        .catch(() => {
          setLoadingTable(false)
          setAplicar(false);
        })
        .finally(() => {
          setLoadingTable(false);
          setAplicar(false);
        });
    },
    []
  );

  function handleAddDeadLine() {
    setIsCreateEdit(true);
    setSelectedId(null);
  };

  const handleApplyFilter = (values) => {
    tableState.current = {
      ...tableState.current,
      pageIndex: 0,
      pessoaId: values.pessoaId?.id || null,
      tipoPrazo: values.tipoPrazo?.id || null,
      statusPrazo: values.statusPrazo?.id || null,
      dataInicio: values.dataInicio || null,
      dataFim: values.dataFim || null,
      dataInicioPublicacao: values.dataInicioPublicacao || null,
      dataFimPublicacao: values.dataFimPublicacao || null,
    };

    setAplicar(true);
    fetchDeadlines({ ...tableState.current });
  };

  const handleDelete = (id) => {
    infoAviso(
      "Deseja realmente deletar essa movimentação do processo?",
      async () => {
        await removeDeadLine(id);
      },
      () => {
        return;
      }
    );
  };

  const removeDeadLine = async (id) => {
    if (id) {
      postNoResponse(`/prazo/delete/${id}`)
        .then(() => {
          successAlert("Prazo removida com sucesso!", () => null);
          fetchDeadlines({ ...tableState });
        })
        .catch(e => {
          console.error(e)
          errorAlert("Erro ao remover prazo");
        })
    }
  }

  const handleCleanFilter = (resetForm) => {
    resetForm();
    setData([]);

    tableState.current = {
      ...tableState.current,
      pageIndex: 0,
      pageSize: 10,
      sorting: [],
      filtered: [],
      pessoaId: null,
      tipoPrazo: null,
      statusPrazo: null,
      dataInicio: null,
      dataFim: null,
      dataInicioPublicacao: null,
      dataFimPublicacao: null,
    };

    fetchDeadlines({ ...tableState.current });
  };

  const backFunction = () => {
    setIsCreateEdit(false);
    setSelectedId(null);
    setViewHistory(false)
  }

  const handleRefresh = () => {
    fetchDeadlines({ ...tableState });
  };

  return (
    <Card id="child-process">

      {isCreateEdit &&
        <Grid item xs={12} md={12}>
          <DeadlinesCreateEdit
            id={selectedId}
            closeCreateEdit={backFunction}
            refreshTable={handleRefresh}
            casoProcessualId={id}
          />
        </Grid>
      }

      {viewHistory &&
        <Grid item xs={12} md={12}>
          <HistoryConsult
            nomeEntidade={table || "c046"}
            idEntidade={selectedId}
            closeCreateEdit={backFunction} />
        </Grid>
      }

      <Grid item xs={12} md={12} sx={{ display: isCreateEdit || viewHistory ? "none" : "" }}>
        <MDBox p={2}>
          <Formik
            initialValues={{
              dataInicio: "",
              dataFim: "",
              dataInicioPublicacao: "",
              dataFimPublicacao: "",
              tipoPrazo: "",
              statusPrazo: "",
              pessoaId: "",
            }}
            onSubmit={values => handleApplyFilter(values)}
          >
            {({ values, setFieldValue, resetForm }) => (
              <Form>
                <Grid container spacing={2} pt={2}>
                  <Grid item xs={12} sm={3}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Inicio</InputLabel>
                    <MDTextFieldCalendar
                      name="dataInicio"
                      type="date"
                      fullWidth
                      value={values.dataInicio}
                      onChange={e => setFieldValue("dataInicio", e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      darkMode={darkMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Fim</InputLabel>
                    <MDTextFieldCalendar
                      name="dataFim"
                      type="date"
                      fullWidth
                      value={values.dataFim}
                      onChange={e => setFieldValue("dataFim", e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      darkMode={darkMode}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Publicação - Início</InputLabel>
                    <MDTextFieldCalendar
                      name="dataInicioPublicacao"
                      type="date"
                      fullWidth
                      value={values.dataInicioPublicacao}
                      onChange={(e) => setFieldValue("dataInicioPublicacao", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      darkMode={darkMode}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Publicação - Fim</InputLabel>
                    <MDTextFieldCalendar
                      name="dataFimPublicacao"
                      type="date"
                      fullWidth
                      value={values.dataFimPublicacao}
                      onChange={(e) => setFieldValue("dataFimPublicacao", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      darkMode={darkMode}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                      Tipo de Prazo
                    </InputLabel>
                    <MDSelect
                      placeholder="Selecione o tipo"
                      name="tipoPrazo"
                      getOptionValue={(option) => option.id}
                      getOptionLabel={(option) => option.name}
                      value={values.tipoPrazo}
                      options={tipoEventoEnum}
                      onChange={(option) => setFieldValue("tipoPrazo", option)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                    <MDSelect
                      name="statusPrazo"
                      placeholder="Selecione o status"
                      getOptionValue={(option) => option.id}
                      getOptionLabel={(option) => option.name}
                      value={values.statusPrazo}
                      options={statusPrazoOptions}
                      onChange={(option) => setFieldValue("statusPrazo", option)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Pessoa</InputLabel>
                    <MDSelect
                      isLoading={isLoadingUsuarios}
                      isMulti={false}
                      getOptionValue={(option) => option.id}
                      getOptionLabel={(option) => option.nome}
                      name="pessoaId"
                      placeholder="Selecione a pessoa"
                      value={values.pessoaId}
                      options={usuarios}
                      onChange={(selectedOption) => {
                        setFieldValue("pessoaId", selectedOption);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} display='flex' justifyContent='space-between'>
                    <Grid item xs={12} sm={12} textAlign="left">
                      <MDButton
                        size="medium"
                        variant="contained"
                        color="info"
                        circular={false}
                        iconOnly={false}
                        onClick={handleAddDeadLine}
                      >
                        Adicionar Prazo
                      </MDButton>
                    </Grid>
                    <Grid item xs={12} sm={12} textAlign="right">
                      <MDButton
                        size="medium"
                        sx={{ marginRight: 2 }}
                        variant="contained"
                        color="info"
                        onClick={() => handleCleanFilter(resetForm)}
                      >
                        Limpar filtro
                      </MDButton>
                      <MDButton size="medium" variant="contained" color="info" type="submit">
                        Aplicar filtro
                      </MDButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </MDBox>

        <MDBox p={2}>
          <DataTable
            loading={loadingTable}
            columnData={columns}
            data={data}
            fetchData={fetchDeadlines}
            totalPages={pages}
            totalRecords={recordsTotal}
            changeFilter={aplicar}
            tableState={tableState}
          />
        </MDBox>
      </Grid>
    </Card>
  );
};

Deadlines.propTypes = {
  id: PropTypes.string,
};

export default memo(Deadlines);
